import type { ScheduleRow } from './types';
import { toBool } from './values';

/**
 * The editor's "Ordering" choice and the three columns it writes. The players
 * read these columns (loop and should_shuffle become the playlist file's flag;
 * resume_from_file_offset decides whether playback resumes). tabasco-react
 * never persisted this choice; this module does.
 */
export type OrderingValue = 'shuffled_resume' | 'ordered_resume' | 'position_1' | 'position_1_no_loop';

/** A saved combination no option describes. Saving it writes none of the three columns. */
export const ORDERING_CUSTOM = 'custom' as const;

export type OrderingChoice = OrderingValue | typeof ORDERING_CUSTOM;

export interface OrderingColumns {
    should_shuffle: boolean;
    resume_from_file_offset: boolean;
    loop: boolean;
}

export interface OrderingOption {
    value: OrderingValue;
    label: string;
    columns: OrderingColumns;
}

export const ORDERING: readonly OrderingOption[] = [
    {
        value: 'shuffled_resume',
        label: 'Shuffled – Resume (Loop)',
        columns: { should_shuffle: true, resume_from_file_offset: true, loop: true },
    },
    {
        value: 'ordered_resume',
        label: 'Ordered – Resume (Loop)',
        columns: { should_shuffle: false, resume_from_file_offset: true, loop: true },
    },
    {
        value: 'position_1',
        label: 'Ordered – Position 1 (Loop)',
        columns: { should_shuffle: false, resume_from_file_offset: false, loop: true },
    },
    {
        value: 'position_1_no_loop',
        label: 'Ordered – Position 1 (No Loop)',
        columns: { should_shuffle: false, resume_from_file_offset: false, loop: false },
    },
] as const;

export const ORDERING_CUSTOM_LABEL = 'Custom (keep current)';

export function orderingLabel(choice: OrderingChoice): string {
    if (choice === ORDERING_CUSTOM) {
        return ORDERING_CUSTOM_LABEL;
    }
    return ORDERING.find((option) => option.value === choice)?.label ?? ORDERING_CUSTOM_LABEL;
}

/**
 * The option a saved row matches. A null column is read as its database
 * default: should_shuffle 1, resume_from_file_offset 1 (also the player's
 * default when the key is absent), loop 1.
 */
export function orderingFromRow(row: ScheduleRow): OrderingChoice {
    const columns: OrderingColumns = {
        should_shuffle: toBool(row.should_shuffle) ?? true,
        resume_from_file_offset: toBool(row.resume_from_file_offset) ?? true,
        loop: toBool(row.loop) ?? true,
    };
    const match = ORDERING.find(
        (option) =>
            option.columns.should_shuffle === columns.should_shuffle &&
            option.columns.resume_from_file_offset === columns.resume_from_file_offset &&
            option.columns.loop === columns.loop,
    );
    return match ? match.value : ORDERING_CUSTOM;
}

/** The columns to write for a choice. "Custom" writes nothing, so the saved values stay. */
export function orderingToColumns(choice: OrderingChoice): Partial<OrderingColumns> {
    const option = ORDERING.find((candidate) => candidate.value === choice);
    return option ? { ...option.columns } : {};
}
